/**
 * Per-frequency asset scope grid for the `frequency_assets` wizard step (IP-15488 / IP-15612).
 *
 * Rows are the inspectable categories the series contains, columns are the series'
 * frequencies (rarest first), cells are checkboxes. A React island inside the Rails
 * `form_for`: state is written back through hidden inputs whose names are ordinary
 * nested-attribute paths (see `serializeScope`).
 *
 * Every visible string arrives as a prop from
 * `InspectionSeries::FrequencyAssetsFacade`, so there is no JS-side i18n to keep in sync.
 *
 * A frequency with nothing checked is allowed -- task-01 made that state deliberately
 * reachable ("this frequency inspects nothing") -- so the column header shows a
 * warning icon and tooltip rather than blocking.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Popup, Table, TableSize, Text, Tooltip } from '@ui';
import { cellKey, displayedKeys, initialCheckedState, isColumnEmpty, isGroupFullyChecked, isGroupUnchecked, isRowFullyChecked, isRowUnchecked, serializeScope } from './serializeScope';
const FREQUENCY_COLUMN_PREFIX = 'frequency-';
const CATEGORY_COLUMN_KEY = 'category';
/**
 * Context rather than closures baked into the column definitions: `Table` treats
 * `column.renderer` as a component *type*, so rebuilding it on every keystroke of state
 * would unmount and remount every cell. Reading state through context keeps the renderer
 * identities stable while still re-rendering on change.
 */
const GridContext = createContext(null);
function useGrid() {
    const context = useContext(GridContext);
    if (!context)
        throw new Error('FrequencyAssetsGrid context is missing');
    return context;
}
/** `category_key` contains slashes; HTML ids must not. */
function domSafe(value) {
    return value.replace(/[^a-zA-Z0-9_-]/g, '-');
}
function frequencyIdFromDataKey(dataKey) {
    return Number(dataKey.slice(FREQUENCY_COLUMN_PREFIX.length));
}
function interpolate(template, vars) {
    return Object.entries(vars).reduce((result, [key, value]) => result.replace(`%{${key}}`, value), template);
}
function GroupTitle({ group }) {
    const { checked, frequencyIds, toggleGroup } = useGrid();
    const categoryKeys = group.rows.map((row) => row.key);
    const allChecked = isGroupFullyChecked(categoryKeys, frequencyIds, checked);
    const noneChecked = isGroupUnchecked(categoryKeys, frequencyIds, checked);
    return (React.createElement(Checkbox, { id: `frequency-assets-group-check-${domSafe(group.key)}`, checked: allChecked, indeterminate: !allChecked && !noneChecked, onChange: (next) => toggleGroup(categoryKeys, next), onClick: (event) => event.stopPropagation(), label: group.label }));
}
function CategoryLabelCell({ item }) {
    const { checked, frequencyIds, labels, toggleRow } = useGrid();
    const allChecked = isRowFullyChecked(item.key, frequencyIds, checked);
    const noneChecked = isRowUnchecked(item.key, frequencyIds, checked);
    return (React.createElement("span", { style: { alignItems: 'center', display: 'inline-flex', gap: '0.8rem' } },
        React.createElement(Checkbox, { id: `frequency-assets-row-${domSafe(item.key)}`, checked: allChecked, indeterminate: !allChecked && !noneChecked, onChange: (next) => toggleRow(item.key, next), label: "", "aria-label": interpolate(labels.rowToggle, { type: item.label }) }),
        React.createElement(Text, { variant: "body" }, item.label),
        React.createElement(Tooltip, { content: labels.countTooltip },
            React.createElement(Text, { variant: "body" },
                "(",
                item.count,
                ")"))));
}
function ScopeCell({ item, dataKey }) {
    var _a;
    const { checked, frequencies, labels, toggleCell } = useGrid();
    const frequencyId = frequencyIdFromDataKey(dataKey);
    const frequency = frequencies.find((column) => column.frequencyId === frequencyId);
    const frequencyLabel = (_a = frequency === null || frequency === void 0 ? void 0 : frequency.label) !== null && _a !== void 0 ? _a : '';
    return (React.createElement(Checkbox, { id: `frequency-assets-${frequencyId}-${domSafe(item.key)}`, checked: Boolean(checked[cellKey(frequencyId, item.key)]), onChange: (next) => toggleCell(frequencyId, item.key, next), label: "", "aria-label": interpolate(labels.rowCheckbox, { type: item.label, frequency: frequencyLabel }) }));
}
function ColumnHeader({ frequency }) {
    const { checked, keys, labels, toggleColumn } = useGrid();
    const isEmpty = isColumnEmpty(frequency, keys, checked);
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const header = (React.createElement("span", { style: { alignItems: 'center', display: 'inline-flex', gap: '0.4rem' } },
        React.createElement(Text, { variant: "xx-small" }, frequency.label),
        isEmpty && (React.createElement("i", { className: "fa-solid fa-triangle-exclamation", role: "img", "aria-label": labels.emptyScope, style: { color: 'var(--yellow-05, #ffd25a)', fontSize: '1.2rem' } })),
        React.createElement("button", { ref: anchorRef, type: "button", className: "qmb-ui-table__header-menu-btn", "aria-label": frequency.label, "aria-haspopup": "true", "aria-expanded": open, onClick: () => setOpen((current) => !current) },
            React.createElement("i", { className: "fa-light fa-ellipsis-vertical", "aria-hidden": "true" })),
        React.createElement(Popup, { anchor: anchorRef.current, show: open, onClose: () => setOpen(false), position: "bottom-right" },
            React.createElement(Popup.Section, null,
                React.createElement(Popup.ActionList, null,
                    React.createElement(Button, { type: "button", onClick: () => {
                            toggleColumn(frequency.frequencyId, true);
                            setOpen(false);
                        } }, labels.selectAll),
                    React.createElement(Button, { type: "button", onClick: () => {
                            toggleColumn(frequency.frequencyId, false);
                            setOpen(false);
                        } }, labels.selectNone))))));
    if (!isEmpty) {
        return header;
    }
    return (React.createElement(Tooltip, { content: labels.emptyScope, position: "bottom", multiline: true }, header));
}
export function FrequencyAssetsGrid({ attributeName, frequencies, groups, labels }) {
    const keys = useMemo(() => displayedKeys(groups), [groups]);
    const frequencyIds = useMemo(() => frequencies.map((frequency) => frequency.frequencyId), [frequencies]);
    const [checked, setChecked] = useState(() => initialCheckedState(frequencies));
    const toggleCell = useCallback((frequencyId, categoryKey, next) => {
        setChecked((previous) => (Object.assign(Object.assign({}, previous), { [cellKey(frequencyId, categoryKey)]: next })));
    }, []);
    const toggleColumn = useCallback((frequencyId, next) => {
        setChecked((previous) => {
            const updated = Object.assign({}, previous);
            keys.forEach((key) => {
                updated[cellKey(frequencyId, key)] = next;
            });
            return updated;
        });
    }, [keys]);
    const toggleRow = useCallback((categoryKey, next) => {
        setChecked((previous) => {
            const updated = Object.assign({}, previous);
            frequencyIds.forEach((frequencyId) => {
                updated[cellKey(frequencyId, categoryKey)] = next;
            });
            return updated;
        });
    }, [frequencyIds]);
    const toggleGroup = useCallback((categoryKeys, next) => {
        setChecked((previous) => {
            const updated = Object.assign({}, previous);
            categoryKeys.forEach((key) => {
                frequencyIds.forEach((frequencyId) => {
                    updated[cellKey(frequencyId, key)] = next;
                });
            });
            return updated;
        });
    }, [frequencyIds]);
    const resetToAll = useCallback(() => {
        setChecked((previous) => {
            const updated = Object.assign({}, previous);
            keys.forEach((key) => {
                frequencyIds.forEach((frequencyId) => {
                    updated[cellKey(frequencyId, key)] = true;
                });
            });
            return updated;
        });
    }, [keys, frequencyIds]);
    const contextValue = useMemo(() => ({
        checked,
        keys,
        frequencyIds,
        frequencies,
        labels,
        toggleCell,
        toggleColumn,
        toggleRow,
        toggleGroup
    }), [checked, keys, frequencyIds, frequencies, labels, toggleCell, toggleColumn, toggleRow, toggleGroup]);
    const columns = useMemo(() => [
        {
            dataKey: CATEGORY_COLUMN_KEY,
            label: labels.categoryColumn,
            columnType: 'primary',
            renderer: CategoryLabelCell
        },
        ...frequencies.map((frequency) => ({
            dataKey: `${FREQUENCY_COLUMN_PREFIX}${frequency.frequencyId}`,
            label: React.createElement(ColumnHeader, { frequency: frequency }),
            align: 'center',
            renderer: ScopeCell
        }))
    ], [frequencies, labels.categoryColumn]);
    const dataGroups = useMemo(() => groups.map((group) => ({
        id: group.key,
        title: React.createElement(GroupTitle, { group: group }),
        data: group.rows.map((row) => (Object.assign(Object.assign({}, row), { id: row.key })))
    })), [groups]);
    const hiddenInputs = useMemo(() => serializeScope({ attributeName, frequencies, keys, checked }), [attributeName, frequencies, keys, checked]);
    return (React.createElement(GridContext.Provider, { value: contextValue },
        React.createElement("div", { "data-frequency-assets-grid": true },
            React.createElement("div", { style: {
                    alignItems: 'flex-start',
                    display: 'flex',
                    gap: '1.6rem',
                    width: '100%'
                } },
                React.createElement("div", { style: { flex: '0 0 60%', minWidth: 0 } },
                    React.createElement(Table, { data: dataGroups, columns: columns, isGrouped: true, collapsibleGroups: true, collapseGroupLabel: labels.collapseGroup, expandGroupLabel: labels.expandGroup, variant: "detail", size: TableSize.Full, tableId: "frequency-assets" })),
                React.createElement("div", { style: { display: 'flex', flex: '1 1 40%', justifyContent: 'flex-end' } },
                    React.createElement(Button, { type: "button", variant: "secondary", onClick: resetToAll }, labels.resetToAll))),
            hiddenInputs.map((input) => (React.createElement("input", { key: input.name, type: "hidden", name: input.name, value: input.value, readOnly: true }))))));
}
export default FrequencyAssetsGrid;
